/* Wistia integration scoped to shoppable cards, including dynamic editor sections. */
(() => {
  if (customElements.get('cst-wistia-shopping')) return;
  let runtimePromise;
  let activePopup = null;
  const cards = new Set();
  const ensureRuntime = () => {
    if (customElements.get('wistia-player')) return Promise.resolve();
    if (!runtimePromise) {
      runtimePromise = new Promise((resolve, reject) => {
        let script = document.querySelector('script[src="https://fast.wistia.com/player.js"]');
        const timer = setTimeout(() => reject(new Error('Wistia load timed out')), 20000);
        const fail = () => { clearTimeout(timer); reject(new Error('Wistia script unavailable')); };
        if (!script) {
          script = document.createElement('script');
          script.src = 'https://fast.wistia.com/player.js';
          script.async = true;
          script.addEventListener('error', fail, { once: true });
          document.head.appendChild(script);
        } else {
          script.addEventListener('error', fail, { once: true });
        }
        customElements.whenDefined('wistia-player').then(() => {
          clearTimeout(timer);
          resolve();
        });
      });
    }
    return runtimePromise;
  };

  class ShoppingPlayer extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      cards.add(this);
      this.isPopup = false;
      this.generation = (this.generation || 0) + 1;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.resumeWanted = this.autoplayEnabled;
      this.inView = false;
      this.ready = false;
      this.started = false;
      this.loading = false;
      this.systemPause = false;
      this.mount = this.querySelector('[data-wistia-mount]');
      this.cover = this.querySelector('[data-wistia-cover]');
      this.error = this.querySelector('[data-wistia-error]');
      this.dialog = this.querySelector('[data-wistia-dialog]');
      this.trigger = this.querySelector('[data-wistia-popup-trigger]');
      this.closeButton = this.querySelector('[data-wistia-close]');
      this.popupEnabled = this.dataset.popup === 'true' && !!this.dialog?.showModal;
      this.dialog?.setAttribute('open', '');
      if (this.trigger) this.trigger.hidden = !this.popupEnabled;
      if (this.trigger) this.trigger.disabled = true;
      this.openClick = () => this.openPopup();
      this.closeClick = () => this.closePopup();
      this.dialogClose = () => { if (!this.dialog.open) this.restorePreview(); };
      this.dialogCancel = event => { event.preventDefault(); this.closePopup(); };
      this.backdropClick = event => {
        if (!this.isPopup || event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closePopup();
      };
      this.trigger?.addEventListener('click', this.openClick);
      this.closeButton?.addEventListener('click', this.closeClick);
      this.dialog?.addEventListener('close', this.dialogClose);
      this.dialog?.addEventListener('cancel', this.dialogCancel);
      this.dialog?.addEventListener('click', this.backdropClick);
      if (this.cover) this.cover.hidden = false;
      if (this.error) { this.error.hidden = true; this.error.style.display = 'none'; }
      this.coverClick = () => {
        if (this.popupEnabled) { this.openPopup(); return; }
        this.resumeWanted = true;
        if (this.cover) this.cover.hidden = true;
        if (this.ready) this.play();
        else this.load();
      };
      this.cover?.addEventListener('click', this.coverClick);
      this.visibilityChange = () => {
        if (document.hidden) this.pauseForVisibility();
        else if ((this.inView || this.isPopup) && this.resumeWanted) this.play();
      };
      document.addEventListener('visibilitychange', this.visibilityChange);
      if ('IntersectionObserver' in window) {
        this.preloadObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.load();
            this.preloadObserver.disconnect();
          }
        }, { rootMargin: '200px' });
        this.preloadObserver.observe(this);
        this.viewObserver = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          if (!this.inView && !this.isPopup) this.pauseForVisibility();
          else if (!document.hidden && this.resumeWanted) this.play();
        });
        this.viewObserver.observe(this);
      } else {
        this.inView = true;
        this.load();
      }
    }

    load() {
      if (this.loading || this.player || !this.connected) return;
      const mediaId = this.dataset.mediaId.trim();
      if (!/^[a-zA-Z0-9]+$/.test(mediaId)) { this.showError(); return; }
      this.loading = true;
      const generation = this.generation;
      ensureRuntime().then(() => {
        if (!this.connected || this.generation !== generation) return;
        const player = document.createElement('wistia-player');
        this.player = player;
        const options = {
          'media-id': mediaId,
          'autoplay': String(this.autoplayEnabled && this.inView && !document.hidden),
          'silent-autoplay': String(this.autoplayEnabled),
          'muted': String(this.autoplayEnabled),
          'end-video-behavior': this.autoplayEnabled ? 'loop' : 'default',
          'fit-strategy': 'cover',
          'preload': this.autoplayEnabled ? 'auto' : 'metadata',
          'resumable': 'false',
          'big-play-button': 'true',
          'play-pause-control': 'true',
          'play-bar-control': String(!this.autoplayEnabled),
          'volume-control': String(!this.autoplayEnabled || this.dataset.soundControl === 'true'),
          'fullscreen-control': String(!this.autoplayEnabled),
          'settings-control': String(!this.autoplayEnabled),
          'controls-visible-on-load': 'true',
          'aria-label': this.dataset.label || 'Shoppable video'
        };
        Object.entries(options).forEach(([key, value]) => player.setAttribute(key, value));
        player.style.cssText = 'display:block;position:absolute;inset:0;width:100%;height:100%;';
        player.addEventListener('api-ready', () => {
          if (!this.connected || this.generation !== generation) return;
          this.ready = true;
          clearTimeout(this.readyTimer);
          if (this.trigger) this.trigger.disabled = false;
          if (this.inView && !document.hidden && this.resumeWanted) this.play();
          else this.pauseForVisibility();
        });
        player.addEventListener('can-play', () => {
          if (!this.connected || this.generation !== generation) return;
          if ((this.autoplayEnabled || this.isPopup) && this.resumeWanted) this.play();
        });
        player.addEventListener('play', () => {
          if (!this.connected || this.generation !== generation) return;
          this.started = true;
          this.resumeWanted = true;
          if (this.cover) this.cover.hidden = true;
          if ((!this.inView && !this.isPopup) || document.hidden || (activePopup && activePopup !== this)) this.pauseForVisibility();
        });
        player.addEventListener('pause', () => {
          if (!this.connected || this.generation !== generation) return;
          if (this.systemPause) this.systemPause = false;
          else if (this.started) {
            this.resumeWanted = false; // Respect a visitor's explicit pause.
            player.autoplay = false;
          }
        });
        player.addEventListener('ended', () => {
          if (!this.connected || this.generation !== generation) return;
          if (!this.autoplayEnabled) this.resumeWanted = false;
        });
        player.addEventListener('error', () => {
          if (this.connected && this.generation === generation) this.showError();
        });
        this.readyTimer = setTimeout(() => { if (!this.ready) this.showError(); }, 30000);
        this.mount.appendChild(player);
      }).catch(() => {
        if (this.connected && this.generation === generation) this.showError();
      });
    }

    play() {
      if (!this.ready || (!this.inView && !this.isPopup) || document.hidden || (activePopup && activePopup !== this)) return;
      if (this.player.state === 'playing') return;
      if (this.autoplayEnabled) this.player.autoplay = true;
      this.systemPause = false;
      try {
        const result = this.player.play();
        // Browsers may block autoplay; native player controls remain available.
        if (result?.catch) result.catch(() => {});
      } catch (_) { /* Visitor can still use Wistia's native play control. */ }
    }

    pauseForVisibility() {
      if (this.player && this.autoplayEnabled) this.player.autoplay = false;
      if (!this.ready || this.player.state === 'beforeplay' || this.player.paused === true) return;
      this.systemPause = true;
      this.player.pause();
    }

    openPopup() {
      if (!this.popupEnabled || !this.ready || this.isPopup) return;
      if (activePopup && activePopup !== this) activePopup.closePopup();
      this.previewResume = this.resumeWanted;
      const rect = this.getBoundingClientRect();
      this.dialog.style.setProperty('--cst-popup-ratio', String(rect.height ? rect.width / rect.height : 9 / 16));
      this.previousOverflow = document.documentElement.style.overflow;
      this.isPopup = true;
      activePopup = this;
      cards.forEach(card => { if (card !== this) card.pauseForVisibility(); });
      // The player stays in this dialog: opening it in the top layer does not re-embed it.
      this.dialog.removeAttribute('open');
      this.dialog.classList.add('is-popup');
      this.dialog.setAttribute('role', 'dialog');
      try { this.dialog.showModal(); }
      catch (_) { this.restorePreview(); return; }
      document.documentElement.style.overflow = 'hidden';
      this.configurePopup(true);
      if (this.cover) this.cover.hidden = true;
      this.resumeWanted = true;
      // Run unmute/play synchronously in the click event for mobile browsers.
      this.player.silentAutoplay = false;
      this.player.muted = false;
      this.player.volume = 1;
      this.play();
      this.closeButton?.focus({ preventScroll: true });
    }

    configurePopup(open) {
      const player = this.player;
      if (!player) return;
      player.fitStrategy = open ? 'contain' : 'cover';
      player.playBarControl = open || !this.autoplayEnabled;
      player.volumeControl = open || !this.autoplayEnabled || this.dataset.soundControl === 'true';
      player.fullscreenControl = open || !this.autoplayEnabled;
      player.settingsControl = open || !this.autoplayEnabled;
    }

    closePopup() {
      if (!this.isPopup) return;
      // Stop sound immediately; the native close event may be delivered later.
      this.player.muted = true;
      this.pauseForVisibility();
      this.dialog.close();
      this.restorePreview();
    }

    restorePreview() {
      if (!this.isPopup) return;
      this.isPopup = false;
      if (activePopup === this) activePopup = null;
      document.documentElement.style.overflow = this.previousOverflow || '';
      if (this.player) {
        this.player.muted = true;
        this.player.silentAutoplay = this.autoplayEnabled;
        this.configurePopup(false);
      }
      this.dialog.classList.remove('is-popup');
      this.dialog.setAttribute('role', 'presentation');
      if (this.connected) this.dialog.setAttribute('open', '');
      this.resumeWanted = this.autoplayEnabled && this.previewResume;
      if (this.connected && this.inView && this.resumeWanted && !document.hidden) this.play();
      if (this.connected && this.trigger) this.trigger.focus({ preventScroll: true });
      cards.forEach(card => {
        if (card.connected && card.inView && card.resumeWanted && !document.hidden) card.play();
      });
    }

    showError() {
      clearTimeout(this.readyTimer);
      if (this.error) {
        this.error.hidden = false;
        this.error.style.display = 'flex';
      }
    }

    disconnectedCallback() {
      this.connected = false;
      cards.delete(this);
      if (this.isPopup) this.closePopup();
      this.trigger?.removeEventListener('click', this.openClick);
      this.closeButton?.removeEventListener('click', this.closeClick);
      this.dialog?.removeEventListener('close', this.dialogClose);
      this.dialog?.removeEventListener('cancel', this.dialogCancel);
      this.dialog?.removeEventListener('click', this.backdropClick);
      this.generation = (this.generation || 0) + 1;
      clearTimeout(this.readyTimer);
      this.preloadObserver?.disconnect();
      this.viewObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.visibilityChange);
      this.cover?.removeEventListener('click', this.coverClick);
      if (this.ready) this.player?.pause();
      this.player?.remove();
      this.player = null;
      this.ready = false;
    }
  }
  customElements.define('cst-wistia-shopping', ShoppingPlayer);
})();
